import type { IconName, StatusKind } from "@sagot/ui";
import { consoleStage } from "../shared/ingestion";
import { pagesProgress, syncPhase, type SyncSource } from "../shared/sync";
import type { SourceView } from "../shared/types";

export interface TypeVisual {
  icon: IconName;
  tile: string;
}

const PDF: TypeVisual = { icon: "file-text", tile: "bg-danger-soft text-danger" };
const SHEET: TypeVisual = { icon: "file-spreadsheet", tile: "bg-success-soft text-success" };
const IMAGE: TypeVisual = { icon: "image", tile: "bg-info-soft text-info" };
const DOC: TypeVisual = { icon: "file", tile: "bg-primary-soft text-primary-ink" };

const BY_TYPE: Readonly<Record<string, TypeVisual>> = {
  website: { icon: "globe", tile: "bg-primary-soft text-primary-ink" },
  qa: { icon: "help-circle", tile: "bg-accent-soft text-accent-ink" },
  text: { icon: "scroll-text", tile: "bg-surface-2 text-fg-muted" },
};

const BY_FILE: Readonly<Record<string, TypeVisual>> = { pdf: PDF, xlsx: SHEET, csv: SHEET, png: IMAGE, jpg: IMAGE, jpeg: IMAGE, image: IMAGE, scan: IMAGE };

/** Icon tile for the header (file type first, then source type). */
export function typeVisual(source: Pick<SourceView, "type" | "file_type">): TypeVisual {
  const byType = BY_TYPE[source.type];
  if (byType) return byType;
  return (source.file_type ? BY_FILE[source.file_type.toLowerCase()] : undefined) ?? DOC;
}

export interface StatusVisual {
  kind: StatusKind;
  icon: IconName;
  /** i18n key under `deployKnowledge.sourceDetail`. */
  labelKey: string;
  /** Interpolation values of the label (page counts, failed pages). */
  values?: Record<string, number>;
}

/** Status badge = icon + text + colour; processing shows the live stage or the page count. */
export function statusVisual(source: Pick<SourceView, "status" | "stage"> & Pick<SyncSource, "sync">): StatusVisual {
  const phase = syncPhase(source);
  if (phase === "ready") return { kind: "success", icon: "check-circle", labelKey: "status.ready" };
  if (phase === "warnings") return { kind: "warning", icon: "alert-triangle", labelKey: "status.readyWithWarnings" };
  if (phase === "failed") return { kind: "danger", icon: "x-circle", labelKey: "status.failed" };
  if (phase === "disabled") return { kind: "neutral", icon: "pause", labelKey: "status.disabled" };
  const pages = pagesProgress(source);
  if (pages) return { kind: "info", icon: "refresh-cw", labelKey: "status.processingPages", values: { done: pages.settled, total: pages.total } };
  return { kind: "info", icon: "refresh-cw", labelKey: `stages.${consoleStage(source)}` };
}

export function isPdf(source: Pick<SourceView, "file_type">): boolean {
  return source.file_type?.toLowerCase() === "pdf";
}

/** A PDF page preview is a `…#page=N` URL (iframe); anything else is a page image. */
export function isPdfPreview(url: string): boolean {
  return /#page=\d+$/.test(url);
}
